import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  Input,
  Button,
  Stack,
  Typography,
} from '@mui/joy';
import type { AreaServed } from '../types';

interface ServiceAreaModalProps {
  open: boolean;
  onClose: () => void;
  serviceArea?: AreaServed | null;
  onSave: (data: Omit<AreaServed, 'id'>) => void;
  loading?: boolean;
}

export const ServiceAreaModal: React.FC<ServiceAreaModalProps> = ({
  open,
  onClose,
  serviceArea,
  onSave,
  loading = false,
}) => {
  const [formData, setFormData] = useState<Omit<AreaServed, 'id'>>({
    city: '',
    state: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (serviceArea) {
      setFormData({
        city: serviceArea.city,
        state: serviceArea.state,
      });
    } else {
      setFormData({
        city: '',
        state: '',
      });
    }
    setErrors({});
  }, [serviceArea, open]);

  const handleInputChange = (field: keyof Omit<AreaServed, 'id'>, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    
    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: 400 }}>
        <DialogTitle>
          {serviceArea ? 'Edit Service Area' : 'Add Service Area'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <FormControl error={!!errors.city}>
              <FormLabel>City</FormLabel>
              <Input
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="Enter city name"
                autoFocus
              />
              {errors.city && (
                <Typography level="body-xs" color="danger">
                  {errors.city}
                </Typography>
              )}
            </FormControl>
            
            <FormControl error={!!errors.state}>
              <FormLabel>State</FormLabel>
              <Input
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                placeholder="Enter state (e.g., TX, NY)"
                slotProps={{
                  input: {
                    maxLength: 2,
                    style: { textTransform: 'uppercase' },
                  },
                }}
              />
              {errors.state && (
                <Typography level="body-xs" color="danger">
                  {errors.state}
                </Typography>
              )}
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="solid"
            color="primary"
            onClick={handleSubmit}
            loading={loading}
          >
            {serviceArea ? 'Update' : 'Add'} Service Area
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};